import type { FieldIssue } from './types';

const CUSTOM: readonly { match: RegExp; message: string; field?: string }[] = [
  {
    match: /endsOn must follow startsOn/i,
    message: 'La fecha de fin debe ser posterior a la de inicio.',
    field: 'endsOn',
  },
  {
    match: /must lie inside the school year/i,
    message: 'El periodo debe estar dentro de las fechas del año escolar.',
    field: 'startsOn',
  },
  {
    match: /overlaps "(.+)"/i,
    message: 'El periodo se cruza con "$1".',
    field: 'startsOn',
  },
  {
    match: /maxScore must exceed minScore/i,
    message: 'La nota máxima debe superar la mínima.',
    field: 'maxScore',
  },
  {
    match: /passMark must be within the scale/i,
    message: 'La nota de aprobación debe estar dentro de la escala.',
    field: 'passMark',
  },
  { match: /already exists/i, message: 'Ya existe un registro con ese nombre.', field: 'name' },
  {
    match: /still has/i,
    message: 'No se puede eliminar: todavía tiene elementos que dependen de él.',
  },
  { match: /main sede cannot be deleted/i, message: 'La sede principal no se puede eliminar.' },
  { match: /Nothing to update/i, message: 'No hay cambios que guardar.' },
];

/**
 * Turns an English validation message (from the shared Zod refinements or the API) into the
 * Spanish the screen shows, and names the field it belongs to when the message implies one.
 *
 * @param {string} message -- The raw message.
 * @param {string | undefined} path -- The field the issue came with, if any.
 * @returns The localized issue.
 */
export const localizeIssue = (message: string, path?: string): FieldIssue => {
  for (const rule of CUSTOM) {
    const match = message.match(rule.match);
    if (match) {
      return {
        field: path || rule.field || null,
        message: rule.message.replace('$1', match[1] ?? ''),
      };
    }
  }
  return { field: path || null, message };
};

/**
 * Spanish for a Zod issue, by code: the schemas are shared with the API, so the same rules
 * validate the form before it is sent.
 *
 * @param {object} issue -- A Zod issue (`code`, `path`, `message`, limits).
 * @returns The localized issue.
 */
export const localizeZodIssue = (issue: {
  code: string;
  path: readonly PropertyKey[];
  message: string;
  minimum?: number | bigint;
  maximum?: number | bigint;
  expected?: string;
  received?: string;
}): FieldIssue => {
  const field = issue.path.map(String).join('.') || null;
  switch (issue.code) {
    case 'too_small':
      return {
        field,
        message:
          issue.minimum === undefined || Number(issue.minimum) <= 1
            ? 'Obligatorio.'
            : `Mínimo ${String(issue.minimum)}.`,
      };
    case 'too_big':
      return { field, message: `Máximo ${String(issue.maximum ?? '')}.` };
    case 'invalid_type':
      return {
        field,
        message: issue.expected === 'number' ? 'Debe ser un número.' : 'Obligatorio.',
      };
    case 'invalid_format':
      return { field, message: 'Formato no válido.' };
    case 'invalid_value':
      return { field, message: 'Elija una opción válida.' };
    default:
      return localizeIssue(issue.message, field ?? undefined);
  }
};
